import React from 'react';

export interface ButtonProps {
    children: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
    variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
    icon?: React.ReactNode;
    className?: string;
    type?: 'button' | 'submit' | 'reset';
}

export const Button: React.FC<ButtonProps> = ({
                                                  children,
                                                  onClick,
                                                  disabled = false,
                                                  loading = false,
                                                  variant = 'primary',
                                                  size = 'md',
                                                  icon,
                                                  className = '',
                                                  type = 'button',
                                              }) => {
    const isDisabled = disabled || loading;

    // Size classes
    const getSizeClasses = () => {
        switch (size) {
            case 'sm':
                return 'px-3 py-1.5 text-xs';
            case 'md':
                return 'px-4 py-2 text-sm';
            case 'lg':
                return 'px-6 py-3 text-base';
            default:
                return 'px-4 py-2 text-sm';
        }
    };

    // Variant classes
    const getVariantClasses = () => {
        if (isDisabled) {
            return 'text-gray-400 bg-gray-200 cursor-not-allowed border-gray-200';
        }

        switch (variant) {
            case 'primary':
                return 'text-white bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 focus:ring-blue-500';
            case 'success':
                return 'text-white bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 focus:ring-green-500';
            case 'danger':
                return 'text-white bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700 focus:ring-red-500';
            case 'secondary':
                return 'text-gray-700 bg-white hover:bg-gray-50 border-gray-300 hover:border-gray-400 focus:ring-blue-500';
            case 'ghost':
                return 'text-gray-600 bg-transparent hover:bg-gray-50 border-transparent hover:border-gray-200 focus:ring-blue-500';
            default:
                return 'text-white bg-blue-600 hover:bg-blue-700 border-blue-600 hover:border-blue-700 focus:ring-blue-500';
        }
    };

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={isDisabled}
            className={`
        inline-flex items-center justify-center font-medium rounded-md border transition-colors
        focus:outline-none focus:ring-2 focus:ring-offset-2
        ${getSizeClasses()}
        ${getVariantClasses()}
        ${className}
      `}
        >
            {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
            )}
            {!loading && icon && <span className="mr-2">{icon}</span>}
            {children}
        </button>
    );
};

// Specific button variants for common use cases
export const PrimaryButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
    <Button {...props} variant="primary" />
);

export const SecondaryButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
    <Button {...props} variant="secondary" />
);

export const SuccessButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
    <Button {...props} variant="success" />
);

export const DangerButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
    <Button {...props} variant="danger" />
);

export const GhostButton: React.FC<Omit<ButtonProps, 'variant'>> = (props) => (
    <Button {...props} variant="ghost" />
);

export default Button;
